import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { useAdminStore, useFilteredUsers } from '../../stores/adminStore';
import { useUIStore } from '../../stores/uiStore';
import { adminApi } from '../../api/admin';
import { Spinner } from '../../components/ui/Spinner';
import { CreateUserModal } from '../../components/admin/CreateUserModal';
import clsx from 'clsx';

export function AdminUsersPage() {
  const { t } = useTranslation('admin');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { isLoading, setUsers, setIsLoading, searchQuery, setSearchQuery } =
    useAdminStore();
  const filteredUsers = useFilteredUsers();
  const addToast = useUIStore((s) => s.addToast);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setIsLoading(true);
    try {
      const { users } = await adminApi.listUsers();
      setUsers(users);
    } catch (error) {
      addToast({
        type: 'error',
        title: tCommon('error'),
        message: t('users.loadError'),
      });
    } finally {
      setIsLoading(false);
    }
  }

  function getPlanBadgeColor(planTier: string, source: string, isAdmin: boolean) {
    // Admins get a special amber badge
    if (isAdmin) {
      return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
    }
    if (planTier === 'pro') {
      return source === 'manual'
        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    }
    return 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400';
  }

  function getStatusBadgeColor(status: string) {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'expired':
      case 'canceled':
        return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400';
    }
  }

  function formatDate(dateString: string | null) {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  }

  function isExpiringSoon(expiresAt: string | null | undefined) {
    if (!expiresAt) return false;
    const daysUntilExpiry = Math.ceil(
      (new Date(expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
    );
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  }

  function isExpired(expiresAt: string | null | undefined) {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('users.title')}
        </h1>
        <p className="text-gray-500 dark:text-slate-400 mt-1">
          {t('users.subtitle')}
        </p>
      </div>

      {/* Actions bar */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Search */}
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder={t('users.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Create button */}
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          <span>{t('users.createUser')}</span>
        </motion.button>
      </div>

      {/* Users table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12">
          <UserCircleIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 dark:text-slate-400">
            {searchQuery
              ? t('users.noResults')
              : t('users.noUsers')}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-900/50 border-b border-gray-200 dark:border-slate-700">
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('users.columns.name')}
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('users.columns.email')}
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('users.columns.plan')}
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('users.columns.status')}
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    {t('users.columns.expires')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                {filteredUsers.map((user) => {
                  const sub = user.subscription;
                  const isAdmin = user.role === 'admin';
                  const expiresAt =
                    sub?.source === 'manual'
                      ? sub.manualExpiresAt
                      : sub?.currentPeriodEnd;

                  return (
                    <motion.tr
                      key={user.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      onClick={() => navigate(`/admin/users/${user.id}`)}
                      className="hover:bg-gray-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                            <span className="text-sm font-medium text-primary-700 dark:text-primary-400">
                              {user.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {user.name}
                            </div>
                            {isAdmin && (
                              <span className="text-xs text-primary-600 dark:text-primary-400">
                                Admin
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-slate-300">
                        {user.email}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={clsx(
                            'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
                            isAdmin
                              ? getPlanBadgeColor('pro', 'manual', true)
                              : sub
                                ? getPlanBadgeColor(sub.planTier, sub.source, false)
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                          )}
                        >
                          {isAdmin ? (
                            <>Admin ∞</>
                          ) : sub ? (
                            <>
                              {sub.planTier === 'pro' ? 'Pro' : 'Starter'}
                              {sub.source === 'manual' && (
                                <span className="text-[10px] opacity-75">
                                  (Manual)
                                </span>
                              )}
                            </>
                          ) : (
                            <>Sin licencia</>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={clsx(
                            'inline-flex px-2.5 py-1 rounded-full text-xs font-medium',
                            isAdmin
                              ? getStatusBadgeColor('active')
                              : sub
                                ? getStatusBadgeColor(
                                    isExpired(expiresAt) ? 'expired' : sub.status
                                  )
                                : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400'
                          )}
                        >
                          {isAdmin ? 'active' : sub ? (isExpired(expiresAt) ? 'expired' : sub.status) : '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {isAdmin ? (
                          <span className="text-amber-600 dark:text-amber-400 font-medium">
                            ∞
                          </span>
                        ) : sub && expiresAt ? (
                          <span
                            className={clsx(
                              'text-sm',
                              isExpired(expiresAt) &&
                                'text-red-600 dark:text-red-400',
                              isExpiringSoon(expiresAt) &&
                                !isExpired(expiresAt) &&
                                'text-yellow-600 dark:text-yellow-400',
                              !isExpired(expiresAt) &&
                                !isExpiringSoon(expiresAt) &&
                                'text-gray-600 dark:text-slate-300'
                            )}
                          >
                            {formatDate(expiresAt)}
                            {isExpired(expiresAt) && ' (Expired)'}
                            {isExpiringSoon(expiresAt) &&
                              !isExpired(expiresAt) &&
                              ' (Soon)'}
                            {sub.source === 'google_play' && (
                              <span className="text-[10px] opacity-75 ml-1">
                                (Google Play)
                              </span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-slate-500">
                            {sub?.source === 'google_play' ? 'Google Play' : '-'}
                          </span>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      <CreateUserModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={loadUsers}
      />
    </div>
  );
}
